import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, X, Check, ArrowRight } from "lucide-react";
import { API } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { Eyebrow } from "./BridalSections";

const MAX_FILES = 5;
const MAX_BYTES = 8 * 1024 * 1024;
const WHO = ["Bride", "Bridesmaids", "Mother of the Bride", "Mother of the Groom", "Flower girl(s)", "Other guests"];
const EMPTY = {
  full_name: "", email: "", phone: "", wedding_date: "", wedding_venue: "",
  getting_ready_location: "", ceremony_time: "", departure_time: "", people_count: "",
  service_location: "", wears_extensions: "", extensions_interest: "", about_wedding: "",
};

const today = () => new Date().toISOString().slice(0, 10);

export function validateBridal(f, consent) {
  const e = {};
  if (!f.full_name.trim()) e.full_name = "Please enter your full name";
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) e.email = "Please enter a valid email address";
  if (!/^[+0-9][0-9\s().-]{5,}$/.test(f.phone.trim())) e.phone = "Please enter a valid mobile number";
  if (!f.wedding_date) e.wedding_date = "Please choose your wedding date";
  else if (f.wedding_date < today()) e.wedding_date = "Your wedding date must be in the future";
  if (!f.wedding_venue.trim()) e.wedding_venue = "Please enter your wedding venue";
  if (!f.getting_ready_location.trim()) e.getting_ready_location = "Please enter your getting-ready address or postcode";
  if (!f.ceremony_time) e.ceremony_time = "Please enter your ceremony time";
  const n = Number(f.people_count);
  if (!n || n < 1 || n > 50) e.people_count = "Please enter a number between 1 and 50";
  if (!consent) e.consent = "Please confirm you understand this enquiry does not reserve your date";
  return e;
}

const Field = ({ label, name, error, required, children, hint }) => (
  <label className="block" data-field={name}>
    <span className="text-[10px] uppercase tracking-[0.24em] text-[#2A1F27]/60">
      {label}{required && <span className="text-[#9C7F45]"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
    {hint && !error && <span className="mt-1.5 block text-[11px] text-[#2A1F27]/45">{hint}</span>}
    {error && <span data-testid={`bridal-error-${name}`} className="mt-1.5 block text-xs text-[#A0324B]">{error}</span>}
  </label>
);

const inputCls = (err) =>
  `w-full bg-transparent border-0 border-b ${err ? "border-[#A0324B]" : "border-[#2A1F27]/20"} px-0 py-3 text-base sm:text-sm text-[#2A1F27] placeholder:text-[#2A1F27]/30 focus:outline-none focus:ring-0 focus:border-[#9C7F45] transition-colors duration-300`;

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
    className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs border transition-colors duration-300 ${
      active ? "bg-[#2A1F27] text-[#FAF7F2] border-[#2A1F27]" : "border-[#2A1F27]/20 text-[#2A1F27]/75 hover:border-[#2A1F27]/50"
    }`}>
    {active && <Check size={12} />}{children}
  </button>
);

const ChoiceRow = ({ value, options, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([v, l]) => (
      <Chip key={v} active={value === v} onClick={() => onChange(value === v ? "" : v)} testId={`${prefix}-${v}`}>{l}</Chip>
    ))}
  </div>
);

function InspirationUpload({ files, setFiles }) {
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const add = (list) => {
    const incoming = Array.from(list || []);
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) { toast.error(`You can upload up to ${MAX_FILES} images`); break; }
      if (!f.type.startsWith("image/") && !/\.(heic|heif)$/i.test(f.name)) { toast.error(`${f.name} is not an image`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} is larger than 8 MB`); continue; }
      next.push(f);
    }
    setFiles(next);
  };

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {previews.map((u, i) => (
          <div key={u} className="relative aspect-square bg-[#EFE9E0]" data-testid={`bridal-upload-preview-${i}`}>
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button type="button" aria-label="Remove image" data-testid={`bridal-upload-remove-${i}`}
              onClick={() => setFiles(files.filter((_, j) => j !== i))}
              className="absolute top-1 right-1 w-7 h-7 rounded-full bg-[#2A1F27]/80 text-white flex items-center justify-center">
              <X size={13} />
            </button>
          </div>
        ))}
        {files.length < MAX_FILES && (
          <label data-testid="bridal-upload-dropzone"
            className="aspect-square border border-dashed border-[#2A1F27]/25 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#9C7F45] transition-colors duration-300"
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}>
            <Upload size={16} strokeWidth={1.3} className="text-[#9C7F45]" />
            <span className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#2A1F27]/55">Add photo</span>
            <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="bridal-upload-input"
              onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
      <p className="mt-2 text-[11px] text-[#2A1F27]/45" data-testid="bridal-upload-count">{files.length} / {MAX_FILES} images · up to 8 MB each</p>
    </div>
  );
}

export function BridalSuccess({ form, reference }) {
  return (
    <div data-testid="bridal-success" className="py-10 text-left sm:text-center">
      <div className="w-14 h-14 border border-[#B89A5E] rounded-full flex items-center justify-center sm:mx-auto"><Check size={20} className="text-[#9C7F45]" /></div>
      <h2 className="mt-8 font-display text-4xl sm:text-5xl text-[#2A1F27]">{form.success_heading}</h2>
      <p className="mt-6 text-sm sm:text-base leading-[1.9] text-[#2A1F27]/70 max-w-xl sm:mx-auto">{form.success_body}</p>
      <div className="mt-8 text-[10px] uppercase tracking-[0.28em] text-[#2A1F27]/50">Your reference</div>
      <div data-testid="bridal-success-reference" className="mt-2 font-display text-3xl text-[#9C7F45]">{reference}</div>
      <p className="mt-8 text-xs text-[#2A1F27]/55 max-w-md sm:mx-auto">{form.booking_note}</p>
    </div>
  );
}

export default function BridalEnquiryForm({ form: copy, services }) {
  const [f, setF] = useState(EMPTY);
  const [who, setWho] = useState([]);
  const [svc, setSvc] = useState([]);
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [hp, setHp] = useState("");

  const set = (k) => (e) => { setF((s) => ({ ...s, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };
  const setVal = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (list, setList) => (v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validateBridal(f, consent);
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = document.querySelector(`[data-field="${Object.keys(errs)[0]}"]`);
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Please check the highlighted fields");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => { if (v !== "") fd.append(k, typeof v === "string" ? v.trim() : v); });
    fd.append("who_requires_hair", JSON.stringify(who));
    fd.append("services_required", JSON.stringify(svc));
    fd.append("consent", "true");
    if (hp) fd.append("website", hp);
    files.forEach((file) => fd.append("inspiration", file));
    try {
      const r = await axios.post(`${API}/bridal/enquire`, fd);
      setReference(r.data?.reference);
      try { track("bridal_enquiry", { people: f.people_count }); } catch (err) { console.warn("track failed", err); }
      setTimeout(() => document.querySelector('[data-testid="bridal-success"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your enquiry — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) return <BridalSuccess form={copy} reference={reference} />;

  return (
    <form onSubmit={submit} noValidate data-testid="bridal-enquiry-form" className="space-y-14">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={hp} onChange={(e) => setHp(e.target.value)}
        className="absolute -left-[9999px] w-px h-px opacity-0" />
      <fieldset className="space-y-8">
        <legend className="font-display text-2xl text-[#2A1F27] mb-6">About you</legend>
        <Field label="Full name" name="full_name" required error={errors.full_name}>
          <input data-testid="bridal-input-full-name" className={inputCls(errors.full_name)} value={f.full_name} onChange={set("full_name")} autoComplete="name" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-8">
          <Field label="Email" name="email" required error={errors.email}>
            <input data-testid="bridal-input-email" type="email" inputMode="email" className={inputCls(errors.email)} value={f.email} onChange={set("email")} autoComplete="email" />
          </Field>
          <Field label="Mobile number" name="phone" required error={errors.phone}>
            <input data-testid="bridal-input-phone" type="tel" inputMode="tel" className={inputCls(errors.phone)} value={f.phone} onChange={set("phone")} autoComplete="tel" />
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-8">
        <legend className="font-display text-2xl text-[#2A1F27] mb-6">Your wedding</legend>
        <div className="grid sm:grid-cols-2 gap-8">
          <Field label="Wedding date" name="wedding_date" required error={errors.wedding_date}>
            <input data-testid="bridal-input-wedding-date" type="date" min={today()} className={inputCls(errors.wedding_date)} value={f.wedding_date} onChange={set("wedding_date")} />
          </Field>
          <Field label="Wedding venue" name="wedding_venue" required error={errors.wedding_venue}>
            <input data-testid="bridal-input-venue" className={inputCls(errors.wedding_venue)} value={f.wedding_venue} onChange={set("wedding_venue")} />
          </Field>
          <Field label="Ceremony time" name="ceremony_time" required error={errors.ceremony_time}>
            <input data-testid="bridal-input-ceremony-time" type="time" className={inputCls(errors.ceremony_time)} value={f.ceremony_time} onChange={set("ceremony_time")} />
          </Field>
          <Field label="Approx. departure time" name="departure_time" hint="When you need to leave for the ceremony, if known">
            <input data-testid="bridal-input-departure-time" type="time" className={inputCls()} value={f.departure_time} onChange={set("departure_time")} />
          </Field>
        </div>
        <Field label="Where would you like your hair done?" name="service_location">
          <ChoiceRow value={f.service_location} onChange={setVal("service_location")} prefix="bridal-location"
            options={[["mobile", "Mobile — come to me"], ["salon", "In salon"], ["unsure", "Not sure yet"]]} />
        </Field>
        <Field label="Getting-ready address or postcode" name="getting_ready_location" required error={errors.getting_ready_location}>
          <input data-testid="bridal-input-getting-ready" className={inputCls(errors.getting_ready_location)} value={f.getting_ready_location} onChange={set("getting_ready_location")} />
        </Field>
      </fieldset>

      <fieldset className="space-y-8">
        <legend className="font-display text-2xl text-[#2A1F27] mb-6">Your bridal party</legend>
        <Field label="How many people need hair (including the bride)?" name="people_count" required error={errors.people_count}>
          <input data-testid="bridal-input-people-count" type="number" inputMode="numeric" min={1} max={50} className={`${inputCls(errors.people_count)} max-w-[140px]`} value={f.people_count} onChange={set("people_count")} />
        </Field>
        <Field label="Who requires hair?" name="who_requires_hair">
          <div className="flex flex-wrap gap-2">
            {WHO.map((w, i) => <Chip key={w} active={who.includes(w)} onClick={() => toggle(who, setWho)(w)} testId={`bridal-who-${i}`}>{w}</Chip>)}
          </div>
        </Field>
        <Field label="Services you're interested in" name="services_required">
          <div className="flex flex-wrap gap-2">
            {services.map((s, i) => <Chip key={s} active={svc.includes(s)} onClick={() => toggle(svc, setSvc)(s)} testId={`bridal-service-${i}`}>{s}</Chip>)}
          </div>
        </Field>
        <div className="grid sm:grid-cols-2 gap-8">
          <Field label="Do you currently wear extensions?" name="wears_extensions">
            <ChoiceRow value={f.wears_extensions} onChange={setVal("wears_extensions")} prefix="bridal-wears" options={[["yes", "Yes"], ["no", "No"]]} />
          </Field>
          <Field label="Interested in bridal extensions?" name="extensions_interest">
            <ChoiceRow value={f.extensions_interest} onChange={setVal("extensions_interest")} prefix="bridal-ext-interest" options={[["yes", "Yes"], ["no", "No"], ["unsure", "Not sure"]]} />
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-8">
        <legend className="font-display text-2xl text-[#2A1F27] mb-6">Your vision</legend>
        <Field label="Inspiration photos (optional)" name="inspiration">
          <InspirationUpload files={files} setFiles={setFiles} />
        </Field>
        <Field label="Tell me about your wedding" name="about_wedding" hint="Your dress, the look you love, anything I should know">
          <textarea data-testid="bridal-input-about" rows={4} maxLength={2000} className={`${inputCls()} resize-none`} value={f.about_wedding} onChange={set("about_wedding")} />
        </Field>
      </fieldset>

      <div data-field="consent">
        <label className="flex items-start gap-4 cursor-pointer">
          <input data-testid="bridal-consent-checkbox" type="checkbox" checked={consent}
            onChange={(e) => { setConsent(e.target.checked); setErrors((er) => ({ ...er, consent: undefined })); }}
            className="mt-1 w-4 h-4 accent-[#2A1F27] shrink-0" />
          <span className="text-xs sm:text-sm leading-[1.8] text-[#2A1F27]/75">{copy.booking_note}</span>
        </label>
        {errors.consent && <span data-testid="bridal-error-consent" className="mt-2 block text-xs text-[#A0324B]">{errors.consent}</span>}
      </div>

      <button type="submit" disabled={submitting} data-testid="bridal-submit-btn"
        className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#2A1F27] text-[#FAF7F2] px-10 py-5 text-xs uppercase tracking-[0.24em] transition-colors duration-300 hover:bg-[#9C7F45] disabled:opacity-60">
        {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending{files.length ? " & uploading photos" : ""}…</> : <>Send my enquiry <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
}

export const BridalFormSection = ({ form, services, formRef }) => (
  <section id="bridal-enquiry" ref={formRef} data-testid="bridal-enquiry-section" className="py-20 sm:py-32 scroll-mt-24">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-32">
          <Eyebrow>{form.eyebrow}</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl text-[#2A1F27] mt-6 leading-[1.08]">{form.heading}</h2>
          <p className="mt-6 text-sm leading-[1.9] text-[#2A1F27]/65 max-w-sm">{form.intro}</p>
        </div>
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        <BridalEnquiryForm form={form} services={services} />
      </div>
    </div>
  </section>
);
